var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AppConfig from '../../config/config';
import { pickVideoSource } from '../../utils/pickVideoSource';
const COLOR_OPTION_NAMES = new Set(['color', 'colour', 'couleur', 'farbe']);
function parseStorefrontVariantLegacyId(gid) {
    const m = gid.match(/ProductVariant\/(\d+)\s*$/);
    if (!m)
        return undefined;
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? undefined : n;
}
function extractSizeFromSelectedOptions(options) {
    if (!(options === null || options === void 0 ? void 0 : options.length))
        return null;
    const lower = (s) => s.toLowerCase().trim();
    const sizeOpt = options.find(o => {
        const n = lower(o.name);
        return n === 'size' || n.includes('size') || n === 'talla' || n === 'taille';
    });
    if (sizeOpt)
        return sizeOpt.value.trim() || null;
    const nonColor = options.filter(o => !COLOR_OPTION_NAMES.has(lower(o.name)));
    if (nonColor.length === 1)
        return nonColor[0].value.trim() || null;
    return null;
}
function buildSizeVariantsFromStorefrontProduct(node) {
    var _a, _b, _c;
    const edges = (_b = (_a = node.variants) === null || _a === void 0 ? void 0 : _a.edges) !== null && _b !== void 0 ? _b : [];
    const bySize = new Map();
    for (const { node: v } of edges) {
        const size = extractSizeFromSelectedOptions((_c = v.selectedOptions) !== null && _c !== void 0 ? _c : []);
        if (!size)
            continue;
        const variantId = parseStorefrontVariantLegacyId(v.id);
        if (variantId == null)
            continue;
        const available = v.availableForSale === true;
        const prev = bySize.get(size);
        if (!prev) {
            bySize.set(size, { size, variantId, available });
        }
        else if (!prev.available && available) {
            bySize.set(size, { size, variantId, available });
        }
    }
    return Array.from(bySize.values()).sort((a, b) => a.size.localeCompare(b.size, undefined, { numeric: true }));
}
const QUERY = `
  query ColourwayProducts($ids: [ID!]!) {
    nodes(ids: $ids) {
      __typename
      ... on Product {
        id
        handle
        title
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        media(first: 4) {
          nodes {
            mediaContentType
            alt
            previewImage { url }
            ... on MediaImage {
              image { url altText }
            }
            ... on Video {
              sources { url mimeType width height }
            }
          }
        }
        variants(first: 100) {
          edges {
            node {
              id
              availableForSale
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;
/** Normalize a Storefront `media` connection into the same shape ProductCard consumes. */
function mapStorefrontMedia(nodes) {
    var _a, _b, _c, _d, _e;
    if (!Array.isArray(nodes))
        return [];
    const result = [];
    for (const m of nodes) {
        if ((m === null || m === void 0 ? void 0 : m.mediaContentType) === 'IMAGE' && ((_a = m.image) === null || _a === void 0 ? void 0 : _a.url)) {
            result.push({ url: m.image.url, altText: (_c = (_b = m.image.altText) !== null && _b !== void 0 ? _b : m.alt) !== null && _c !== void 0 ? _c : null });
        }
        else if ((m === null || m === void 0 ? void 0 : m.mediaContentType) === 'VIDEO' && m.sources) {
            const picked = pickVideoSource(m.sources);
            if (picked === null || picked === void 0 ? void 0 : picked.url) {
                result.push({
                    url: picked.url,
                    altText: (_d = m.alt) !== null && _d !== void 0 ? _d : null,
                    video_thumbnail: (_e = m.previewImage) === null || _e === void 0 ? void 0 : _e.url,
                });
            }
        }
        if (result.length >= 2)
            break;
    }
    return result;
}
const CHUNK_SIZE = 50;
export function fetchColourwayProductsByIds(ids) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const result = new Map();
        const domain = AppConfig.searchSettings.shopify.domain;
        const token = AppConfig.searchSettings.shopify.storefrontToken;
        if (!(domain === null || domain === void 0 ? void 0 : domain.trim()) || !(token === null || token === void 0 ? void 0 : token.trim()) || ids.length === 0) {
            return result;
        }
        const url = `https://${domain}/api/2024-10/graphql.json`;
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            const chunk = ids.slice(i, i + CHUNK_SIZE);
            try {
                const response = yield fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': token,
                    },
                    body: JSON.stringify({ query: QUERY, variables: { ids: chunk } }),
                });
                if (!response.ok)
                    continue;
                const json = (yield response.json());
                if (json.errors)
                    continue;
                const nodes = (_b = (_a = json.data) === null || _a === void 0 ? void 0 : _a.nodes) !== null && _b !== void 0 ? _b : [];
                for (const node of nodes) {
                    if (!node || node.__typename !== 'Product' || !node.id || !node.title || !node.handle)
                        continue;
                    const min = (_c = node.priceRange) === null || _c === void 0 ? void 0 : _c.minVariantPrice;
                    const images = mapStorefrontMedia((_d = node.media) === null || _d === void 0 ? void 0 : _d.nodes);
                    const sizeVariants = buildSizeVariantsFromStorefrontProduct(node);
                    result.set(node.id, {
                        title: node.title,
                        handle: node.handle,
                        priceAmount: (_e = min === null || min === void 0 ? void 0 : min.amount) !== null && _e !== void 0 ? _e : '0',
                        compareAtPriceAmount: (_h = (_g = (_f = node.compareAtPriceRange) === null || _f === void 0 ? void 0 : _f.minVariantPrice) === null || _g === void 0 ? void 0 : _g.amount) !== null && _h !== void 0 ? _h : '0',
                        currencyCode: (_j = min === null || min === void 0 ? void 0 : min.currencyCode) !== null && _j !== void 0 ? _j : '',
                        images,
                        sizeVariants,
                    });
                }
            }
            catch (_k) {
                /* network / parse */
            }
        }
        return result;
    });
}
